import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { ListItem } from 'src/app/controls/filters/models/list-item';
import { Attribute } from 'src/app/models/attribute';
import { AttributeOverrideRow } from 'src/app/models/attribute-override-row';
import { OverrideCondition } from 'src/app/models/enums/override-condition';
import { OverridedAttribute } from 'src/app/models/overrided-attribute';
import { ListItemPipe } from '../../../../../controls/pipes/list-item.pipe';
import { AttributeOverrideDialogComponent } from '../attribute-override-dialog/attribute-override-dialog.component';
import { AttributeOverrideDialogResult } from '../attribute-override-dialog/models/attribute-override-dialog-result';

@Component({
    selector: 'app-attribute-override-table',
    templateUrl: './attribute-override-table.component.html',
    styleUrls: ['./attribute-override-table.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: AttributeOverrideTableComponent,
            multi: true,
        },
    ],
    imports: [FaIconComponent, MatTooltipModule, MatTableModule, MatSortModule, NgClass, MatIconModule, ListItemPipe]
})
export class AttributeOverrideTableComponent implements ControlValueAccessor {
    public faCircleInfo = faCircleInfo;

    /** Liste des colonnes du tableau listant les propriétés. */
    attributesTableColumns: string[] = ['name', 'condition', 'action'];

    /** Liste des surcgharges d'attributs. */
    private _attributesOverride: AttributeOverrideRow[];

    disabled: boolean = false;

    /** Setter permettant de spécifier les surcharges d'attributs et de mettre à jour le tableau.*/
    @Input() set attributesOverride(value: AttributeOverrideRow[]) {
        this._attributesOverride = value;
        this.attributesDatasource = new MatTableDataSource(this.attributesOverride);
    }

    /** Getter permettant de récupérer les surcharges d'attributs */
    get attributesOverride(): AttributeOverrideRow[] {
        return this._attributesOverride;
    }

    /** Emis lorsque la liste des surcharges d'attributs est modifiée. */
    @Output() attributesChanged = new EventEmitter<AttributeOverrideRow[]>();

    /** Datasource du tableau listant les surcharges d'attributs. */
    attributesDatasource: MatTableDataSource<AttributeOverrideRow>;

    onChange: (value?: OverridedAttribute[]) => void;

    onTouch: () => void;

    _attributes: Attribute[] = [];

    _overrideConditions: ListItem<OverrideCondition>[] = null;

    @Input()
    public set attributes(value: Attribute[]) {
        this._attributes = value;
    }

    constructor(public dialog: MatDialog) {
        // Initialise la datasource du tableau listant les propriétés.
        this.attributesDatasource = new MatTableDataSource(this.attributesOverride);

        this._overrideConditions = [];
        this._overrideConditions.push(new ListItem<OverrideCondition>(OverrideCondition.Always, 'Toujours'));
        this._overrideConditions.push(new ListItem<OverrideCondition>(OverrideCondition.FolderAttributeNotFilledOut, "Si l'attribut de destination n'est pas renseigné"));
        this._overrideConditions.push(new ListItem<OverrideCondition>(OverrideCondition.IncomingAttributeFilledOut, "Si l'attribut source est renseigné"));
    }

    // upon UI element value changes, this method gets triggered
    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    // upon touching the element, this method gets triggered
    registerOnTouched(fn: any) {
        this.onTouch = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;

        if (isDisabled) {
            const index = this.attributesTableColumns.findIndex((x) => x === 'action');

            if (index >= 0) {
                this.attributesTableColumns.splice(index, 1);
            }
        } else {
            const index = this.attributesTableColumns.findIndex((x) => x === 'action');

            if (index < 0) {
                this.attributesTableColumns.push('action');
            }
        }
    }

    // this method sets the value programmatically
    writeValue(attributeOverrides: AttributeOverrideRow[]) {
        console.log('value', attributeOverrides);

        // let tab = [];

        // for (let key in attributeOverrides) {
        // 	let index = Number.parseInt(key);
        // 	let attributeOverride = attributeOverrides[index];
        // 	let attributeId = attributeOverride.attributeId
        // 	let overrideCondition = attributeOverride.overrideCondition;
        // 	let attributeName = this._attributes.find(x => x.id == attributeId).header;
        // 	tab.push({ attributeId: attributeId, attributeName: attributeName, overrideCondition: overrideCondition });
        // }

        // this.attributesOverride = tab;
        this.attributesOverride = attributeOverrides;
    }

    /**
     * Emis lorsque l'utilisateur supprime une surcharge d'attribut.
     * @param attributeId: Id de l'attribut à supprimer.
     */
    public onDeleteAttribute(attributeId: number) {
        const index = this.attributesOverride.findIndex((x) => x.attributeId === attributeId);
        this.attributesOverride.splice(index, 1);
        this.attributesDatasource = new MatTableDataSource(this.attributesOverride);
        this.applyChange();
    }

    /**
     * Emis lorsque l'utilisateur edite une surcharge d'attribut.
     * @param attributeId: Id de l'attribut à éditer.
     */
    public onEditAttribute(attributeOverrideRow: AttributeOverrideRow) {
        console.log("Édition de l'attribut", attributeOverrideRow);

        const dialogRef = this.dialog.open(AttributeOverrideDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Édition de l'attribut",
                row: attributeOverrideRow,
                attributes: this._attributes,
                overrideConditions: this._overrideConditions,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: AttributeOverrideDialogResult) => {
            if (dialogResult.confirmation) {
                // Mise à jour de la surcharge d'attribut.
                const index = this.attributesOverride.findIndex((x) => x.attributeId === dialogResult.value.attributeId);
                this.attributesOverride.splice(index, 1, dialogResult.value);
                this.attributesDatasource = new MatTableDataSource(this.attributesOverride);
                this.applyChange();
            }
        });
    }

    /**
     * Emis lorsque l'utilisateur ajoute une surcharge d'attribut.
     */
    public onAddAttribute() {
        console.log("Ajout d'un attribut");

        const dialogRef = this.dialog.open(AttributeOverrideDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Ajout d'un attribut",
                row: null as AttributeOverrideRow,
                attributes: this._attributes,
                overrideConditions: this._overrideConditions,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie
        dialogRef.afterClosed().subscribe((dialogResult: AttributeOverrideDialogResult) => {
            if (dialogResult.confirmation) {
                // Ajout de la surcharge d'attribut.
                this.attributesOverride.push(dialogResult.value);
                this.attributesDatasource = new MatTableDataSource(this.attributesOverride);
                this.applyChange();
            }
        });
    }

    private applyChange(): void {
        this.attributesChanged.emit(this.attributesOverride);

        if (this.onChange) {
            let tab: OverridedAttribute[] = [];

            this.attributesOverride.forEach((x) => {
                let overridedAttribute = new OverridedAttribute();
                overridedAttribute.attributeId = x.attributeId;
                overridedAttribute.condition = x.overrideCondition;

                tab.push(overridedAttribute);
            });

            this.onChange(tab);
        }
    }
}
